import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useTypedNavigate } from "../../lib/typedRouter";
import type { WorkingScopeSearch } from "../../lib/workingScope";
import { useToast } from "../../ui";
import {
  launchStateFromSpec,
  toTemplateSpec,
  type SimulationLaunchState,
} from "../simulations/simulationLaunchState";
import type { LaunchOptionsResult } from "../simulations/useLaunchOptions";
import { TemplateAuthoringForm } from "./TemplateAuthoringForm";
import { useBatchSpecPreview } from "../simulations/useBatchSpecPreview";

type TemplatePatchBody = components["schemas"]["TemplatePatch"];
type TemplateResponse = components["schemas"]["TemplateResponse"];
type TemplatesSearchState = { tab?: "templates" } & WorkingScopeSearch;

export interface LoadedTemplateEditFormProps {
  launchOptions: LaunchOptionsResult;
  template: TemplateResponse;
}

export function LoadedTemplateEditForm({
  launchOptions,
  template,
}: LoadedTemplateEditFormProps) {
  const navigate = useTypedNavigate<TemplatesSearchState>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState<SimulationLaunchState>(() =>
    launchStateFromSpec(
      template.spec ?? null,
      template.display_name,
      launchOptions.variables,
    ),
  );
  const [schedule, setSchedule] = useState(template.schedule ?? "");
  const [scheduleTimezone, setScheduleTimezone] = useState(
    template.schedule_timezone,
  );
  const preview = useBatchSpecPreview(
    state,
    launchOptions.variables,
    !launchOptions.isPending &&
      !launchOptions.isRefreshingVariables &&
      launchOptions.errorMessage === undefined,
  );

  const returnToTemplates = () => {
    void navigate({
      search: ({ agent, team }: WorkingScopeSearch) => ({
        agent,
        tab: "templates" as const,
        team,
      }),
      to: "/simulations",
    });
  };

  const saveTemplate = useMutation({
    mutationFn: async () => {
      const body: TemplatePatchBody = {
        display_name: state.displayName.trim(),
        schedule: schedule.trim().length > 0 ? schedule.trim() : null,
        schedule_timezone: scheduleTimezone.trim(),
        spec: toTemplateSpec(state, launchOptions.variables),
      };
      const { data, error } = await apiClient.PATCH("/v1/templates/{id}", {
        body,
        params: { path: { id: template.id } },
      });
      if (error) throw new ApiRequestError(error);
      return data;
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(queryKeys.templates.detail(saved.id), saved);
      toast.success("Template saved");
      await queryClient.invalidateQueries({
        queryKey: queryKeys.templates.all,
      });
      returnToTemplates();
    },
    onError: (error: unknown) => {
      toast.error("Template save failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  return (
    <TemplateAuthoringForm
      agentId={template.agent_id}
      errorMessage={
        launchOptions.errorMessage ??
        (saveTemplate.isError
          ? requestErrorDescription(saveTemplate.error)
          : undefined)
      }
      isPreviewPending={
        preview.isPending || launchOptions.isRefreshingVariables
      }
      onRetryOptions={
        launchOptions.errorMessage ? launchOptions.retry : undefined
      }
      isSaving={saveTemplate.isPending}
      onCancel={returnToTemplates}
      onChange={setState}
      onSave={() => {
        saveTemplate.mutate();
      }}
      onScheduleChange={setSchedule}
      onScheduleTimezoneChange={setScheduleTimezone}
      options={launchOptions.options}
      preview={preview.preview}
      previewErrorMessage={preview.errorMessage}
      onRetryPreview={preview.retry}
      schedule={schedule}
      scheduleTimezone={scheduleTimezone}
      state={state}
      submitLabel="Save template"
      variables={launchOptions.variables}
    />
  );
}
